import {
    ButtonCancel,
    ButtonSecondary,
    Colors,
    SelectFilled,
    Text,
    type OptionProps,
} from '@amelio/ui-library';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { CascadeModalHeader } from './CascadeModalHeader';

import {
    useDeactivateCompetency,
    useGetCompetencyGroups,
    useGetCompetencyImpact,
    useRemoveCompetency,
} from '@/api/competency-services/competency';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage } from '@/utils/localization';

type CascadeCompetencyModalMode = 'deactivate' | 'delete';

type CascadeCompetencyModalProps = {
    mode: CascadeCompetencyModalMode;
    competencyId: string;
    competencyName: string;
    onClose: () => void;
};

/** Sentinel value for the "no replacement" option. SelectFilled maps it to an empty string. */
const NO_REPLACEMENT = '';

export const CascadeCompetencyModal = ({
    mode,
    competencyId,
    competencyName,
    onClose,
}: CascadeCompetencyModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { handleError } = useError();

    const isDelete = mode === 'delete';

    const {
        data: impact,
        isFetching: isLoadingImpact,
        isError,
    } = useGetCompetencyImpact(competencyId);
    const { data: competencyGroups } = useGetCompetencyGroups();

    const { mutateAsync: deactivateCompetency, isPending: isDeactivating } =
        useDeactivateCompetency();
    const { mutateAsync: removeCompetency, isPending: isRemoving } = useRemoveCompetency();

    const [replacementCompetencyId, setReplacementCompetencyId] = useState<string>(NO_REPLACEMENT);

    const isSubmitting = isDeactivating || isRemoving;

    // Re-wiring profiles/assignments only matters when something is actually affected. Once the
    // impact has loaded, expose the replacement selector only if at least one profile or employee
    // is impacted; while loading (or on error / no data) we keep it hidden so it never flashes in
    // prematurely.
    const hasImpact =
        !isLoadingImpact &&
        !isError &&
        impact != null &&
        (impact.profilesAffected.length > 0 || impact.employeesAffectedCount > 0);

    // The surrounding ui-library Modal panel is `max-h-[90vh] overflow-y-auto`, which both
    // clips the replacement dropdown (issue #1) and forces a permanent scrollbar (issue #2).
    // We can only touch this file, so we reach the panel (our direct parent) and let it size
    // to its content with the dropdown rendered "par-dessus tout". `overflow-y-auto` is kept
    // as a fallback only for viewports too short to fit the content.
    const rootRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        const panel = rootRef.current?.parentElement;
        if (!panel) {
            return;
        }
        const previousOverflow = panel.style.overflow;
        const previousMaxHeight = panel.style.maxHeight;
        panel.style.overflow = 'visible';
        panel.style.maxHeight = 'none';
        return () => {
            panel.style.overflow = previousOverflow;
            panel.style.maxHeight = previousMaxHeight;
        };
    }, []);

    // Other active competencies the user can re-wire profiles/assignments to, with an explicit
    // "no replacement" option pinned to the top so the choice can always be cleared (issue #4).
    const replacementOptions = useMemo<OptionProps[]>(() => {
        const competencies = (competencyGroups ?? [])
            .flatMap((group) => group.competencies)
            .filter(
                (competency) =>
                    competency.id !== competencyId && competency.status === CompetencyStatus.ACTIVE,
            )
            .map((competency) => ({
                id: competency.id,
                label: getTextByLanguage(competency.name, language),
                value: competency.id,
            }));

        return [
            {
                id: NO_REPLACEMENT,
                label: t('competencies.cascade.noReplacementOption'),
                value: NO_REPLACEMENT,
            },
            ...competencies,
        ];
    }, [competencyGroups, competencyId, language, t]);

    // Reset the selection to "no replacement" when the selector is hidden (nothing to re-wire) or
    // when the previously chosen replacement disappears from the list. This keeps the confirm
    // payload sending `null` whenever the selector is not actually shown.
    useEffect(() => {
        if (replacementCompetencyId === NO_REPLACEMENT) {
            return;
        }
        if (
            !hasImpact ||
            !replacementOptions.some((option) => option.value === replacementCompetencyId)
        ) {
            setReplacementCompetencyId(NO_REPLACEMENT);
        }
    }, [hasImpact, replacementOptions, replacementCompetencyId]);

    const onConfirm = async () => {
        const payload = {
            id: competencyId,
            replacementCompetencyId: replacementCompetencyId || null,
        };

        try {
            if (isDelete) {
                await removeCompetency(payload);
                showSuccessToast({ message: t('competencies.cascade.success.delete') });
            } else {
                await deactivateCompetency(payload);
                showSuccessToast({ message: t('competencies.cascade.success.deactivate') });
            }

            onClose();
        } catch (error) {
            handleError({ error });
        }
    };

    // Only the "no replacement" sentinel is present when there is nothing to re-wire to.
    const hasReplacementCandidates = replacementOptions.length > 1;

    return (
        <div
            ref={rootRef}
            className={'flex flex-col gap-4'}
        >
            <CascadeModalHeader
                isDelete={isDelete}
                dataTestId={'competency-cascade-header'}
                title={
                    <>
                        {isDelete
                            ? t('competencies.cascade.title.delete')
                            : t('competencies.cascade.title.deactivate')}

                        {` « ${competencyName} »`}
                    </>
                }
            />

            <div
                className={`mt-4 flex flex-col gap-3 rounded-lg border p-4 ${isDelete ? 'border-error/20 bg-error/10' : 'border-warning/50 bg-warning/10'}`}
            >
                <Text
                    size={'small'}
                    weight={'medium'}
                >
                    <span
                        className={`flex items-start gap-2 ${isDelete ? 'text-red-500' : 'text-warning'}`}
                    >
                        <span aria-hidden={'true'}>⚠️</span>

                        <span>
                            {isDelete
                                ? t('competencies.cascade.warningHeading.delete')
                                : t('competencies.cascade.warningHeading.deactivate')}
                        </span>
                    </span>
                </Text>

                {isLoadingImpact ? (
                    <div className={'flex items-center justify-center py-4'}>
                        <OverlayLoader isLoading={isLoadingImpact} />
                    </div>
                ) : isError ? (
                    <Text
                        size={'small'}
                        color={Colors.ERROR}
                    >
                        {t('competencies.cascade.loadError')}
                    </Text>
                ) : impact ? (
                    <>
                        <Text size={'small'}>
                            <span className={'text-gray-700'}>
                                {t('competencies.cascade.impact', {
                                    profiles: impact.profilesAffected.length,
                                    employees: impact.employeesAffectedCount,
                                })}
                            </span>
                        </Text>

                        <Text size={'small'}>
                            <span className={'text-gray-700'}>
                                {isDelete
                                    ? t('competencies.cascade.warning.delete')
                                    : t('competencies.cascade.warning.deactivate')}
                            </span>
                        </Text>

                        {impact.profilesAffected.length > 0 && (
                            <div className={'flex flex-col gap-2'}>
                                <Text
                                    size={'small'}
                                    weight={'medium'}
                                >
                                    <span className={'text-gray-800'}>
                                        {t('competencies.cascade.affectedProfiles')}
                                    </span>
                                </Text>

                                <div className={'flex flex-wrap gap-2'}>
                                    {impact.profilesAffected.map((profile) => (
                                        <span
                                            key={profile.id}
                                            className={
                                                'bg-purple/10 text-purple rounded-full px-3 py-0.5 text-xs font-semibold'
                                            }
                                        >
                                            {getTextByLanguage(profile.name, language)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>

            {hasImpact && (
                <div className={'flex flex-col gap-2'}>
                    <Text
                        size={'small'}
                        weight={'medium'}
                        color={Colors.NAVY}
                    >
                        {t('competencies.cascade.replaceLabel')}
                    </Text>

                    <SelectFilled
                        options={replacementOptions}
                        value={replacementCompetencyId}
                        onChange={(value) => setReplacementCompetencyId(value)}
                        placeholder={t('competencies.cascade.replacePlaceholder')}
                        sort={'none'}
                        placement={'top-left'}
                        disabled={isSubmitting || !hasReplacementCandidates}
                        dataTestId={'competency-cascade-replacement-select'}
                        className={'text-navy border border-gray-200 bg-white [&_span]:font-normal'}
                    />
                </div>
            )}

            <div className={'mt-2 flex justify-end gap-2'}>
                <ButtonSecondary
                    type={'button'}
                    onClick={onClose}
                    disabled={isSubmitting}
                    size={'md'}
                    fitContent
                    dataTestId={'competency-cascade-cancel'}
                >
                    {t('competencies.cascade.cancel')}
                </ButtonSecondary>

                <ButtonCancel
                    onClick={onConfirm}
                    loading={isSubmitting}
                    disabled={isLoadingImpact || isError}
                    size={'md'}
                    fitContent
                    className={`!text-white ${isDelete ? '!bg-error !border-red-500' : '!border-gray-500 !bg-gray-500'}`}
                    dataTestId={'competency-cascade-confirm'}
                >
                    {isDelete
                        ? t('competencies.cascade.confirm.delete')
                        : t('competencies.cascade.confirm.deactivate')}
                </ButtonCancel>
            </div>
        </div>
    );
};
